import { ReactComponent as FeatureArt1 } from '../../assets/features/feature-1.svg'
import { ReactComponent as FeatureArt2 } from '../../assets/features/feature-2.svg'
import { ReactComponent as FeatureArt3 } from '../../assets/features/feature-3.svg'
import { ReactComponent as FeatureArt4 } from '../../assets/features/feature-4.svg'
import { ReactComponent as FeatureArt5 } from '../../assets/features/feature-5.svg'

// M1.3.3: the five illustrations, lifted out of a Figma section export rather than exported by
// Микола. They are not bitmaps in the file — each is a 472×312 dark card drawn as vector, plus
// (14.08 re-extraction) floating popup badges painted outside that box on five of them — and
// three show a screenshot through a <pattern> fill. Inline SVG rather than <img> is deliberate:
// 147 of the labels inside them are live <text> in Figtree, and an <img> cannot reach the page's
// webfont, so it would silently render them in a fallback face.
// Regenerate with: python3 files/figma-scripts/extract-feature-art.py — the 34MB section export
// it reads is no longer kept in the repo, so re-export the section from Figma first.
const ART = { 1: FeatureArt1, 2: FeatureArt2, 3: FeatureArt3, 4: FeatureArt4, 5: FeatureArt5 }

const FEATURES = [
  {
    title: 'Deal Pipeline & Dashboard',
    eyebrow: 'Keep Every Opportunity Organized',
    body: 'Track, organize, and manage every deal from one place.',
    badge: null,
    img: 1,
  },
  {
    title: 'ARV Analysis',
    eyebrow: ['Estimate property value in minutes.', 'Works in all 50 states.'],
    body: 'Instant ARV estimates backed by ranked comps and intelligent adjustments.',
    badge: null,
    img: 2,
  },
  {
    title: 'Flip & BRRRR Projection',
    eyebrow: 'Model Deals with Fully Controllable Assumptions',
    body: 'Score deals, compare scenarios, and model financing with customizable templates.',
    badge: null,
    img: 3,
  },
  {
    title: 'Rent Comps',
    eyebrow: 'Understand Local Rental Value Instantly',
    body: 'Instant rental estimates backed by comps, pricing trends, and local unit mix.',
    badge: 'COMING SOON',
    // 4, not 5. The old bitmaps were numbered the other way round, so carrying those numbers
    // over put the Deal Presentations artwork on this block. `art` is now the block's own
    // position in the file: the fourth card sits beside the fourth title.
    img: 4,
  },
  {
    title: 'Deal Presentations',
    eyebrow: 'Present Every Deal Professionally',
    body: 'Professional reports that build credibility with buyers, partners, and lenders.',
    badge: 'COMING SOON',
    // 5, see the note above.
    img: 5,
  },
]

const FeatureBlock = ({ title, eyebrow, body, badge, img, imageLeft }) => {
  const Art = ART[img]
  // 472 × 312 is the card's own size in the file. The old bitmaps were 944 × 840 — a different
  // shape entirely, which is why the blocks get shorter and wider here.
  const image = (
    <Art
      role="img"
      aria-label={title}
      className="w-full max-w-[420px] md:max-w-none md:w-[472px] h-auto shrink-0 select-none"
    />
  )
  const text = (
    <div className="w-full md:w-[444px]">
      {/* 13.08 set #4 D1.3: "Coming Soon" moved from above the title to 12px to its right, both
          breakpoints — was `mb-3` stacked above, which is what "should sit to the right" is
          correcting. flex-wrap so a long title + badge still breaks cleanly on mobile widths
          rather than overflowing the 444px/full-width column. */}
      {/* Figma `feature-txt`: title and eyebrow are one text node — 28px/40 extrabold with a
          16px/28 italic 400 override on the eyebrow line, so they stack with no gap between. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* 13.08 set #4 D1.4 + M1.3: "Deal Pipeline & Dashboard" (the longest title) wrapped to
            two lines at mobile widths — 25 characters at 28px extrabold doesn't fit a 327px
            column. Sized down on mobile only; desktop keeps 28px, unaffected.
            14.08 followup: Микола says 24px still fits one row per Figma — raised back from 22.
            Applies to all five cards, same as the 13.08 call to keep one shared mobile scale
            rather than special-casing the longest title. */}
        <h3 className="text-[24px] leading-[30px] md:text-[28px] md:leading-[40px] font-extrabold text-brand-dark">{title}</h3>
        {badge && (
          <span className="inline-block px-3 py-1 rounded-[6px] bg-brand-primary text-white text-[11px] font-bold tracking-wider shrink-0">
            {badge}
          </span>
        )}
      </div>
      {eyebrow && (
        <p className="text-[16px] leading-[28px] font-normal italic text-brand-dark/60">
          {/* Figma fits this on one 444px line at desktop (node height 67 = one 40px title
              line + one 28px eyebrow line), so the split is mobile-only. */}
          {Array.isArray(eyebrow)
            ? eyebrow.map((line, idx) => (
                <span key={idx} className="block md:inline">
                  {line}
                  {idx < eyebrow.length - 1 ? ' ' : ''}
                </span>
              ))
            : eyebrow}
        </p>
      )}
      {/* Figma: body is its own node at 16px/28 weight 600. */}
      <p className="mt-4 text-[16px] leading-[28px] font-semibold text-brand-dark/80">{body}</p>
    </div>
  )

  return (
    // 1.3.4: no gap between the image and the text on mobile. In the export the mobile block
    // is a vertical auto-layout with itemSpacing 0 — the image bottom and the text top share
    // the same y to four decimal places. Desktop is a row, so its gap is horizontal and stays.
    // 13.08 set #4 M1.1: superseded on mobile — 36px between image and heading, not 0.
    <div className={`flex flex-col ${imageLeft ? 'md:flex-row' : 'md:flex-row-reverse'} items-start md:items-center justify-between gap-[36px] md:gap-20`}>
      {image}
      {text}
    </div>
  )
}

const Features = () => {
  return (
    // M1.3.2: this section goes back to the light page fill (#F4FAEF); the copy inside it
    // flips from white to brand-dark to keep its contrast.
    <section id="features" className="bg-brand-pageFill">
      {/* M1.3.1: 48px on mobile / 80px from the Workflow section above, split evenly with
          the 24/40 that Tools contributes on its side of the colour change. */}
      {/* Asymmetric on purpose, and the file is asymmetric here. The 1200 canvas starts at
          x2170 (the hero H1 sits on it); this section's content runs 2257→3253, so 87 left and
          117 right. Three independent things confirm the 996 width: the art boxes of the
          image-left blocks start at 2257, the art boxes of the mirrored blocks end at 3253, and
          "Everything You Need to Analyze Deals" is centred on 2755 — the midpoint of 996, not
          of the full canvas. With 472 + 80 + 444 the row fills it exactly. */}
      <div className="max-w-canvas mx-auto px-6 md:pl-[87px] md:pr-[117px] pt-[24px] md:pt-[40px] pb-[40px] md:pb-[80px]">
        {/* Figma `why-choose`: 40px/40 extrabold, and the node is a single 40px-tall line —
            one row on desktop. Микола's "Row 1 / Row 2" note is under Mobile, so the break is
            mobile-only; without it the mobile wrap lands mid-phrase. */}
        <h2 className="text-[28px] md:text-[40px] leading-[36px] md:leading-[40px] font-extrabold text-center">
          {/* The space sits BEFORE the <br> so it collapses at the mobile line break;
              after the <br> it would survive as a leading space and shift row 2. */}
          <span className="text-brand-primary">Everything You Need</span>{' '}
          <br className="md:hidden" />
          {/* 13.08 set #4 D1.4: solid #17270D, both breakpoints — was brand-dark at 70% opacity
              (a lighter blended shade against the page fill), not a different colour value. */}
          <span className="text-brand-dark">to Analyze Deals</span>
        </h2>

        {/* 13.08 set #4 M1.2: "Each tool should be 72px apart" on mobile — was gap-12 (48px).
            His text names the boundary as image-to-next-heading; the gap that's actually ours to
            set here is the space between one card's last line of body copy and the next card's
            image, since M1.1 already fixes the 36px image-to-own-heading step inside each card.
            Flagged for him to confirm the boundary reading in the reply. */}
        <div className="mt-8 md:mt-12 flex flex-col gap-[72px] md:gap-16">
          {FEATURES.map((feature, i) => (
            <FeatureBlock
              key={feature.title}
              {...feature}
              imageLeft={i % 2 === 0}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Features
